import React, {createContext, useEffect, useState} from 'react';
import {AuthContextType} from "../models/interfaces/auth-context-type.interface";
import {AuthProviderProps} from "../models/interfaces/auth-provider-props.interface";
import AuthService from "../services/auth.service";
import {AxiosError} from "axios";
import {useLocation, useNavigate} from "react-router-dom";
import LocalStorageService from "../../common/services/local-storage.service";
import {LocalStorageKeys} from "@common/models/enums/local-storage-keys.enum";
import {ResetPasswordBodyRequest} from "../models/interfaces/reset-email-body.interface";
import {AuthenticatedUser} from "../models/interfaces/authenticated-user.interface";
import {ChangePasswordBody, ChangePasswordBodyRequest} from "../models/interfaces/change-password-body.interface";
import {Authentication} from "../models/interfaces/authentication.interface";
import {RegisterBody} from "../models/interfaces/register-body.interface";
import {UserAuthority} from "../models/enums/user-authority.enum";

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
    const [isAuthenticated, setIsAuthenticated] = useState(AuthService.isAuthenticated());
    const [currentUser, setCurrentUser] = useState<AuthenticatedUser | undefined>(AuthService.authenticatedUser);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<AxiosError | null>(null);
    const navigate = useNavigate();

    const login = async (email: string, password: string): Promise<void> => {
        setLoading(true);
        setError(null);

        return await AuthService.authenticate(email, password)
            .then(async (): Promise<void> => {
                setCurrentUser(AuthService.authenticatedUser!)
                setIsAuthenticated(true);
            })
            .catch((err): void => {
                setError(err);
                setIsAuthenticated(false);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    };

    const register = async (body: RegisterBody) => {
        setLoading(true);
        setError(null);

        return await AuthService.registerAccount(body)
            .catch((err): void => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    };

    const logout = async () => {
        await AuthService.logout();
        navigate('/login', {replace: true})
    };

    const getTokenToResetPassword = async (email: string, successMessage?: string) => {
        setLoading(true);
        setError(null);

        return await AuthService.getTokenResetPassword(email, successMessage)
            .catch((err): void => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    }

    const resetPassword = async (newPassword: string, token: string): Promise<void> => {
        setLoading(true);
        setError(null);

        const body: ResetPasswordBodyRequest = {
            email: LocalStorageService.get(LocalStorageKeys.EMAIL_RESET_PASSWORD) ?? "",
            token,
            newPassword
        }

        return await AuthService.resetPassword(body)
            .then((): void => {
                navigate('/login', {replace: true})
            })
            .catch((err): void => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    }

    const changePassword = async (body: ChangePasswordBody): Promise<void> => {
        setLoading(true);
        setError(null);

        const mappedBody: ChangePasswordBodyRequest = {
            currentPassword: body.currentPassword,
            newPassword: body.newPassword
        }

        return await AuthService.changePassword(mappedBody)
            .then()
            .catch((err): void => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    }

    const sendEmailConfirmationToken = async (email: string): Promise<void | AxiosError> => {
        setLoading(true);
        setError(null);

        return await AuthService.sendEmailConfirmationToken(email)
            .catch((err: AxiosError) => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    }

    const activateAccount = async (token: string): Promise<void | AxiosError> => {
        setLoading(true);
        setError(null);

        return await AuthService.activateAccount(token)
            .catch((err: AxiosError) => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    }

    const updateUsername = async (username: string): Promise<void | AxiosError> => {
        setLoading(true);
        setError(null);

        return await AuthService.updateUsername(username)
            .catch((err: AxiosError) => {
                setError(err);
                throw err;
            })
            .finally((): void => {
                setLoading(false)
            })
    }

    const updateAuthenticationField = async <K extends keyof Authentication>(key: K, value: Authentication[K]) => {
        if (key === 'user' && typeof value === 'object' && value !== null) {
            const prevUser = currentUser!;

            const updatedUser = {
                ...prevUser,
                ...value as AuthenticatedUser,
            };

            AuthService.updateAuthenticationField(key, updatedUser as Authentication[K]);

            setCurrentUser(updatedUser)
        } else {
            AuthService.updateAuthenticationField(key, value);
        }
    };

    const hasAnyAuthorities = (requiredRoles: UserAuthority[]) => {
        const rolesLoggedUser = [currentUser?.role];
        if (rolesLoggedUser.length) {
            return requiredRoles.some(role => rolesLoggedUser.includes(role));
        } else {
            return false;
        }
    }

    useEffect(() => {
        AuthService.setLogoutCallback(() => {
            setIsAuthenticated(false);
            setCurrentUser(undefined);
        });

        return () => {
            AuthService.setLogoutCallback(null);
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                login,
                logout,
                getTokenToResetPassword,
                resetPassword,
                changePassword,
                register,
                loading,
                error,
                currentUser,
                setCurrentUser,
                updateAuthenticationField,
                sendEmailConfirmationToken,
                activateAccount,
                hasAnyAuthorities,
                updateUsername,
            }}>
            {children}
        </AuthContext.Provider>
    )
};

export {AuthContext, AuthProvider}
